import { useState, useCallback } from 'react';
import { fetchUserOverrides, upsertUserOverride, deleteUserOverride, fetchB2CFeatureMatrix } from '@/lib/api';
import { usePlatformStore } from '@/lib/store';
import { Search, Loader2, Plus, Trash2, User, AlertCircle } from 'lucide-react';
import { cn } from '@/utils/helpers';
import { Modal } from '@/components/shared/UIComponents';
import type { UserOverride, UserOverrideInput, B2CFeature } from '@/types';

interface Props {
  defaultTenantId: string;
}

export function UserOverridesTab({ defaultTenantId }: Props) {
  const { tenants } = usePlatformStore();
  const [selectedTenantId, setSelectedTenantId] = useState(defaultTenantId);
  const [userSearch, setUserSearch] = useState('');
  const [overrides, setOverrides] = useState<UserOverride[]>([]);
  const [features, setFeatures] = useState<B2CFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editOverride, setEditOverride] = useState<UserOverride | null>(null);

  const loadOverrides = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchUserOverrides(selectedTenantId);
      setOverrides(data);
      setSearched(true);

      // Also load features for the modal
      if (features.length === 0) {
        const matrix = await fetchB2CFeatureMatrix(selectedTenantId);
        setFeatures(matrix.features);
      }
    } catch (err) {
      console.error('Failed to load overrides:', err);
    }
    setLoading(false);
  }, [selectedTenantId, features.length]);

  const loadUserOverrides = useCallback(async (userId: string) => {
    setLoading(true);
    try {
      const data = await fetchUserOverrides(selectedTenantId, userId);
      setOverrides(data);
      setSearched(true);

      if (features.length === 0) {
        const matrix = await fetchB2CFeatureMatrix(selectedTenantId);
        setFeatures(matrix.features);
      }
    } catch (err) {
      console.error('Failed to load user overrides:', err);
    }
    setLoading(false);
  }, [selectedTenantId, features.length]);

  const handleSearch = () => {
    if (userSearch.trim()) {
      loadUserOverrides(userSearch.trim());
    } else {
      loadOverrides();
    }
  };

  const handleDelete = async (o: UserOverride) => {
    try {
      await deleteUserOverride(selectedTenantId, o.user_id, o.feature_id);
      setOverrides(prev => prev.filter(x => x.id !== o.id));
    } catch (err) {
      console.error('Failed to delete override:', err);
    }
  };

  const handleSaveOverride = async (input: UserOverrideInput) => {
    try {
      await upsertUserOverride(selectedTenantId, input);
      setShowModal(false);
      setEditOverride(null);
      // Reload
      if (userSearch.trim()) {
        await loadUserOverrides(userSearch.trim());
      } else {
        await loadOverrides();
      }
    } catch (err) {
      console.error('Failed to save override:', err);
    }
  };

  // Group overrides by user
  const groupedByUser: Record<string, UserOverride[]> = {};
  for (const o of overrides) {
    const key = o.user_id;
    if (!groupedByUser[key]) groupedByUser[key] = [];
    groupedByUser[key].push(o);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-surface-500">
        Grant, block, or limit specific features for individual users. Overrides take priority over plan defaults.
      </p>

      <div className="flex gap-3 flex-wrap">
        {/* Tenant selector */}
        <select
          value={selectedTenantId}
          onChange={e => { setSelectedTenantId(e.target.value); setOverrides([]); setSearched(false); }}
          className="input-field py-2 text-sm w-48"
        >
          {tenants.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>

        {/* User ID search */}
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" />
          <input type="text" value={userSearch} onChange={e => setUserSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="User ID or email (blank = all)"
            className="input-field pl-10 py-2 w-full" />
        </div>

        <button onClick={handleSearch}
          className="btn-primary px-4 py-2 text-sm">
          Search
        </button>

        <button onClick={() => { setEditOverride(null); setShowModal(true); }}
          className="flex items-center gap-1 px-4 py-2 text-sm border border-surface-200 rounded-lg hover:bg-surface-50 transition-colors">
          <Plus size={14} /> New Override
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
        </div>
      ) : searched && overrides.length === 0 ? (
        <div className="text-center py-12">
          <AlertCircle className="mx-auto text-surface-400 mb-2" size={24} />
          <p className="text-sm text-surface-500">No overrides found.</p>
          <p className="text-xs text-surface-400 mt-1">Use "New Override" to grant or block a feature for a user.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedByUser).map(([userId, userOverrides]) => {
            const first = userOverrides[0];
            return (
              <div key={userId} className="glass-panel overflow-hidden">
                <div className="flex items-center gap-3 px-5 py-3 border-b border-surface-200">
                  <div className="w-8 h-8 rounded-full bg-brand-500/10 flex items-center justify-center">
                    <User size={14} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-surface-900">
                      {first.user_name || first.user_email || userId}
                    </div>
                    {first.user_email && first.user_name && (
                      <div className="text-xs text-surface-500">{first.user_email}</div>
                    )}
                  </div>
                  <span className="text-xs text-surface-500">{userOverrides.length} override{userOverrides.length > 1 ? 's' : ''}</span>
                </div>

                <div className="divide-y divide-surface-100">
                  {userOverrides.map(o => (
                    <div key={o.id} className="flex items-center gap-4 px-5 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-surface-700">
                            {o.features?.name || o.feature_id}
                          </span>
                          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium',
                            o.is_enabled ? 'badge-emerald' : 'badge-red')}>
                            {o.is_enabled ? 'Enabled' : 'Blocked'}
                          </span>
                          {o.limit_value !== null && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded badge-blue font-medium">
                              Limit: {o.limit_value}
                            </span>
                          )}
                          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium',
                            o.actor_type === 'super_admin' ? 'badge-purple' : 'bg-surface-100 text-surface-500')}>
                            {o.actor_type === 'super_admin' ? 'Super Admin' : 'Tenant Admin'}
                          </span>
                        </div>
                        <div className="flex gap-3 mt-1">
                          {o.reason && <span className="text-[10px] text-surface-500">Reason: {o.reason}</span>}
                          {o.expires_at && <span className="text-[10px] text-surface-500">Expires: {new Date(o.expires_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => { setEditOverride(o); setShowModal(true); }}
                          className="px-2 py-1 text-[10px] text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-500/10 rounded transition-colors">
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(o)}
                          className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded transition-colors">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <OverrideModal
          features={features}
          existing={editOverride}
          onSave={handleSaveOverride}
          onClose={() => { setShowModal(false); setEditOverride(null); }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* OVERRIDE MODAL                                                             */
/* ═══════════════════════════════════════════════════════════════════════════ */

function OverrideModal({ features, existing, onSave, onClose }: {
  features: B2CFeature[];
  existing: UserOverride | null;
  onSave: (input: UserOverrideInput) => Promise<void>;
  onClose: () => void;
}) {
  const [userId, setUserId] = useState(existing?.user_id || '');
  const [featureId, setFeatureId] = useState(existing?.feature_id || '');
  const [isEnabled, setIsEnabled] = useState(existing?.is_enabled ?? true);
  const [limitValue, setLimitValue] = useState<string>(existing?.limit_value?.toString() || '');
  const [reason, setReason] = useState(existing?.reason || '');
  const [expiresAt, setExpiresAt] = useState(existing?.expires_at?.split('T')[0] || '');
  const [saving, setSaving] = useState(false);

  const selectedFeature = features.find(f => f.id === featureId);
  const isCount = selectedFeature?.limit_type === 'count' || selectedFeature?.limit_type === 'count_per_trip';

  const handleSubmit = async () => {
    if (!userId || !featureId) return;
    setSaving(true);
    await onSave({
      user_id: userId,
      feature_id: featureId,
      is_enabled: isEnabled,
      limit_value: limitValue ? parseInt(limitValue, 10) : null,
      reason: reason || undefined,
      expires_at: expiresAt ? `${expiresAt}T23:59:59Z` : null,
      actor_type: 'super_admin',
    });
    setSaving(false);
  };

  return (
    <Modal open title={existing ? 'Edit Override' : 'Create User Override'} onClose={onClose}>
      <div className="space-y-4">
        {/* User ID */}
        <div>
          <label className="block text-xs font-medium text-surface-700 mb-1">User ID</label>
          <input type="text" value={userId} onChange={e => setUserId(e.target.value)}
            disabled={!!existing}
            placeholder="UUID of the user"
            className="input-field py-2 w-full text-sm" />
        </div>

        {/* Feature */}
        <div>
          <label className="block text-xs font-medium text-surface-700 mb-1">Feature</label>
          <select value={featureId} onChange={e => setFeatureId(e.target.value)}
            disabled={!!existing}
            className="input-field py-2 w-full text-sm">
            <option value="">Select a feature...</option>
            {features.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.category})</option>
            ))}
          </select>
        </div>

        {/* Enable/Disable */}
        <div className="flex items-center justify-between">
          <label className="text-sm text-surface-700">Enable Feature</label>
          <button onClick={() => setIsEnabled(!isEnabled)}
            className={cn('relative w-10 h-5 rounded-full transition-colors',
              isEnabled ? 'bg-brand-500' : 'bg-surface-200')}>
            <span className={cn('block w-4 h-4 rounded-full bg-white shadow transform transition-transform mt-0.5',
              isEnabled ? 'translate-x-5 ml-0.5' : 'translate-x-0.5')} />
          </button>
        </div>

        {/* Limit value (for count features) */}
        {isCount && (
          <div>
            <label className="block text-xs font-medium text-surface-700 mb-1">
              Limit Value
              <span className="text-surface-400 ml-1">(leave blank for unlimited)</span>
            </label>
            <input type="number" value={limitValue} onChange={e => setLimitValue(e.target.value)}
              placeholder="∞"
              className="input-field py-2 w-32 text-sm" />
          </div>
        )}

        {/* Reason */}
        <div>
          <label className="block text-xs font-medium text-surface-700 mb-1">Reason</label>
          <input type="text" value={reason} onChange={e => setReason(e.target.value)}
            placeholder="Optional — why this override exists"
            className="input-field py-2 w-full text-sm" />
        </div>

        {/* Expiry */}
        <div>
          <label className="block text-xs font-medium text-surface-700 mb-1">Expires At</label>
          <input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
            className="input-field py-2 w-48 text-sm" />
          <p className="text-[10px] text-surface-400 mt-1">Leave blank for permanent override</p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-surface-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-surface-500 hover:text-surface-700 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!userId || !featureId || saving}
            className="btn-primary px-4 py-2 text-sm flex items-center gap-1">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {existing ? 'Update' : 'Create'} Override
          </button>
        </div>
      </div>
    </Modal>
  );
}
